import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  LifeBuoy, Plus, X, Loader2, Clock, CheckCircle2, AlertCircle,
  ChevronDown, MessageSquare,
} from 'lucide-react';
import { clsx } from 'clsx';
import { supportApi } from '@/lib/api';
import { format } from 'date-fns';

const CATEGORIES = [
  { value: 'billing',          label: 'Billing' },
  { value: 'technical',        label: 'Technical Issue' },
  { value: 'feature_request',  label: 'Feature Request' },
  { value: 'data_issue',       label: 'Data / Records Issue' },
  { value: 'other',            label: 'Other' },
] as const;

const PRIORITIES = [
  { value: 'low',      label: 'Low',      color: 'text-gray-500 bg-gray-50' },
  { value: 'medium',   label: 'Medium',   color: 'text-blue-600 bg-blue-50' },
  { value: 'high',     label: 'High',     color: 'text-amber-600 bg-amber-50' },
  { value: 'critical', label: 'Critical', color: 'text-clinical-danger bg-red-50' },
] as const;

const STATUS_STYLES: Record<string, { label: string; icon: any; cls: string }> = {
  open:        { label: 'Open',        icon: AlertCircle,  cls: 'text-amber-600 bg-amber-50' },
  in_progress: { label: 'In Progress', icon: Clock,        cls: 'text-blue-600 bg-blue-50' },
  resolved:    { label: 'Resolved',    icon: CheckCircle2, cls: 'text-clinical-success bg-clinical-success/10' },
  closed:      { label: 'Closed',      icon: CheckCircle2, cls: 'text-gray-500 bg-gray-100' },
};

const createSchema = z.object({
  subject:     z.string().min(5, 'Subject must be at least 5 characters').max(255),
  description: z.string().min(10, 'Please describe the issue').max(5000),
  category:    z.string(),
  priority:    z.string(),
});
type CreateForm = z.infer<typeof createSchema>;

function StatusBadge({ status }: { status: string }) {
  const s = STATUS_STYLES[status] ?? STATUS_STYLES.open;
  const Icon = s.icon;
  return (
    <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium', s.cls)}>
      <Icon size={11}/> {s.label}
    </span>
  );
}

function PriorityBadge({ priority }: { priority: string }) {
  const p = PRIORITIES.find(x => x.value === priority);
  if (!p) return null;
  return (
    <span className={clsx('inline-flex px-2 py-0.5 rounded-full text-xs font-medium', p.color)}>
      {p.label}
    </span>
  );
}

function TicketCard({ ticket }: { ticket: any }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full text-left px-5 py-4 flex items-start justify-between gap-4 hover:bg-gray-50 transition-colors">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <StatusBadge status={ticket.status}/>
            <PriorityBadge priority={ticket.priority}/>
            <span className="text-xs text-gray-400 capitalize">
              {CATEGORIES.find(c => c.value === ticket.category)?.label ?? ticket.category}
            </span>
          </div>
          <p className="font-medium text-navy text-sm truncate">{ticket.subject}</p>
          <p className="text-xs text-gray-400 mt-0.5">
            {format(new Date(ticket.created_at), 'dd MMM yyyy, hh:mm a')}
          </p>
        </div>
        <ChevronDown size={16} className={clsx('text-gray-400 flex-shrink-0 mt-1 transition-transform', open && 'rotate-180')}/>
      </button>

      {open && (
        <div className="px-5 pb-5 border-t border-gray-100">
          <p className="text-sm text-gray-700 mt-4 whitespace-pre-wrap">{ticket.description}</p>

          {ticket.admin_reply ? (
            <div className="mt-4 bg-navy/5 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare size={14} className="text-navy"/>
                <span className="text-xs font-semibold text-navy">BBS Support Reply</span>
                <span className="text-xs text-gray-400 ml-auto">
                  {format(new Date(ticket.updated_at), 'dd MMM yyyy, hh:mm a')}
                </span>
              </div>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{ticket.admin_reply}</p>
            </div>
          ) : (
            <div className="mt-4 text-xs text-gray-400 italic flex items-center gap-1.5">
              <Clock size={12}/> Awaiting response from BBS Support…
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function SupportPage() {
  const [showNew, setShowNew] = useState(false);
  const [filter, setFilter]   = useState<string>('');
  const [apiError, setApiError] = useState<string | null>(null);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['support-tickets', filter],
    queryFn:  () => supportApi.list(filter ? { status: filter } : {}).then(r => r.data),
  });
  const tickets: any[] = data?.tickets ?? [];

  const createMut = useMutation({
    mutationFn: (body: object) => supportApi.create(body),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: ['support-tickets'] });
      setShowNew(false);
      reset();
      setApiError(null);
    },
    onError: (e: any) => setApiError(e.response?.data?.error ?? 'Failed to submit ticket'),
  });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<CreateForm>({
    resolver: zodResolver(createSchema),
    defaultValues: { category: 'technical', priority: 'medium' },
  });

  const field = 'w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange/30 focus:border-orange';
  const lbl   = 'block text-sm font-medium text-navy mb-1.5';
  const errC  = 'text-clinical-danger text-xs mt-1';

  return (
    <div className="p-6 lg:p-8 max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-orange/10 flex items-center justify-center">
            <LifeBuoy size={20} className="text-orange"/>
          </div>
          <div>
            <h1 className="text-xl font-bold text-navy">Support</h1>
            <p className="text-xs text-gray-400">Submit and track support requests to BBS Tech</p>
          </div>
        </div>
        <button
          onClick={() => { setShowNew(true); setApiError(null); }}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-orange text-navy
                     font-semibold text-sm hover:bg-orange-dark transition-colors">
          <Plus size={16}/> New Ticket
        </button>
      </div>

      {/* Status filter */}
      <div className="flex gap-2 flex-wrap">
        {[
          { value: '',            label: 'All' },
          { value: 'open',        label: 'Open' },
          { value: 'in_progress', label: 'In Progress' },
          { value: 'resolved',    label: 'Resolved' },
          { value: 'closed',      label: 'Closed' },
        ].map(f => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={clsx(
              'px-3 py-1.5 rounded-full text-xs font-medium transition-colors',
              filter === f.value
                ? 'bg-navy text-white'
                : 'bg-white border border-gray-200 text-gray-500 hover:border-navy hover:text-navy'
            )}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Ticket list */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 size={24} className="animate-spin text-orange"/>
        </div>
      ) : tickets.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 flex flex-col items-center gap-3">
          <LifeBuoy size={32} className="text-gray-200"/>
          <p className="text-sm text-gray-400">No support tickets yet</p>
          <button onClick={() => setShowNew(true)}
            className="text-orange text-sm hover:underline font-medium">
            Submit your first ticket →
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {tickets.map((t: any) => <TicketCard key={t.id} ticket={t}/>)}
        </div>
      )}

      {/* New Ticket Modal */}
      {showNew && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-lg font-bold text-navy">New Support Ticket</h2>
              <button onClick={() => { setShowNew(false); reset(); }}
                className="text-gray-400 hover:text-navy">
                <X size={20}/>
              </button>
            </div>

            <form onSubmit={handleSubmit(d => createMut.mutate(d))} className="p-6 space-y-4">
              <div>
                <label className={lbl}>Subject *</label>
                <input {...register('subject')} placeholder="Brief description of your issue" className={field}/>
                {errors.subject && <p className={errC}>{errors.subject.message}</p>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={lbl}>Category</label>
                  <select {...register('category')} className={field}>
                    {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className={lbl}>Priority</label>
                  <select {...register('priority')} className={field}>
                    {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className={lbl}>Description *</label>
                <textarea
                  {...register('description')}
                  rows={5}
                  placeholder="Please describe the issue in detail — steps to reproduce, expected vs actual behavior, screenshots if any…"
                  className={`${field} resize-none`}
                />
                {errors.description && <p className={errC}>{errors.description.message}</p>}
              </div>

              {apiError && (
                <div className="bg-red-50 border border-red-200 text-clinical-danger text-sm rounded-lg px-4 py-3">
                  {apiError}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setShowNew(false); reset(); }}
                  className="flex-1 py-2.5 rounded-lg border border-gray-200 text-navy text-sm font-medium hover:bg-gray-50">
                  Cancel
                </button>
                <button type="submit" disabled={createMut.isPending}
                  className="flex-1 py-2.5 rounded-lg bg-orange text-navy font-semibold text-sm
                             hover:bg-orange-dark disabled:opacity-50 flex items-center justify-center gap-2">
                  {createMut.isPending && <Loader2 size={15} className="animate-spin"/>}
                  Submit Ticket
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
